import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { properties } from "../../utils/properties";
import { removeDuplicates } from "../../utils/util";
import { getService } from "../../services/ApiServices";
import { ENDPOINTS } from "../../constants/ApiEndPoints";

const initialState = {
  isCaseStudyLoading: true,
  isTechnologyDataLoading: true,
  isIndustryDataLoading: true,
  caseStudyData: [],
  technologyCategoriesList: [],
  industriesCategoriesList: [],
  currentPage: 1,
  hasMoreData: true,
};

export const getCaseStudyData = createAsyncThunk(
  "service/getCaseStudyData",
  async (caseStudyRequest, thunkAPI) => {
    try {
      const {
        searchValue,
        technologyCategoryId,
        industriesCategoryId,
        structureId,
        page,
        pageSize,
      } = caseStudyRequest;

      let url = `${ENDPOINTS.GET_WEB_CONTENT}?p_auth=${properties.authToken}&page=${page}&pageSize=${pageSize}&sort=datePublished:desc`;
      url = url.replace("{structureId}", structureId);

      let filters = [];
      if (technologyCategoryId) {
        const encodedTechnologyCategoryId = encodeURIComponent(
          `taxonomyCategoryIds/any(t:t eq ${technologyCategoryId})`
        );
        filters.push(encodedTechnologyCategoryId);
      }

      if (industriesCategoryId) {
        const encodedIndustriesCategoryId = encodeURIComponent(
          `taxonomyCategoryIds/any(t:t eq ${industriesCategoryId})`
        );
        filters.push(encodedIndustriesCategoryId);
      }

      if (filters.length > 0) {
        url += `&filter=${filters.join(" and ")}`;
      }

      if (searchValue) {
        url += `&search="${searchValue}"`;
      }

      const response = await getService(url, caseStudyRequest.token);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue("something went wrong");
    }
  }
);

export const getTechnologyCategories = createAsyncThunk(
  "service/getTechnologyCategories",
  async (technologyCategoriesRequest, thunkAPI) => {
    try {
      const response = await getService(
        `${ENDPOINTS.GET_CATEGORIES}${technologyCategoriesRequest.technologiesVocabularyId}${ENDPOINTS.TAXONOMY_CATEGORIES}?p_auth=${technologyCategoriesRequest.token}&pageSize=-1&sort=name:asc`,
        technologyCategoriesRequest.token
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue("something went wrong");
    }
  }
);

export const getIndustryCategories = createAsyncThunk(
  "service/getIndustryCategories",
  async (industriesCategoriesRequest, thunkAPI) => {
    try {
      const response = await getService(
        `${ENDPOINTS.GET_CATEGORIES}${industriesCategoriesRequest.industryVocabularyId}${ENDPOINTS.TAXONOMY_CATEGORIES}?p_auth=${industriesCategoriesRequest.token}&pageSize=-1&sort=name:asc`,
        industriesCategoriesRequest.token
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue("something went wrong");
    }
  }
);
const caseStudiesSlice = createSlice({
  name: "caseStudiesSlice",
  initialState,
  reducers: {
    resetPagination(state) {
      state.currentPage = 1;
      state.caseStudyData = [];
      state.hasMoreData = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCaseStudyData.pending, (state) => {
        state.isCaseStudyLoading = true;
      })
      .addCase(getCaseStudyData.fulfilled, (state, { payload }) => {
        state.isCaseStudyLoading = false;

        if (payload.items.length < 6) {
          state.hasMoreData = false;
        } else {
          state.hasMoreData = true;
        }

        state.caseStudyData = removeDuplicates([
          ...state.caseStudyData,
          ...payload.items,
        ]); // Ensure unique items after appending

        state.currentPage += 1;
      })
      .addCase(getCaseStudyData.rejected, (state) => {
        state.isCaseStudyLoading = false;
      })
      .addCase(getTechnologyCategories.pending, (state) => {
        state.isTechnologyDataLoading = true;
      })
      .addCase(getTechnologyCategories.fulfilled, (state, { payload }) => {
        state.isTechnologyDataLoading = false;
        state.technologyCategoriesList = payload.items;
      })
      .addCase(getTechnologyCategories.rejected, (state) => {
        state.isTechnologyDataLoading = false;
      })
      .addCase(getIndustryCategories.pending, (state) => {
        state.isIndustryDataLoading = true;
      })
      .addCase(getIndustryCategories.fulfilled, (state, { payload }) => {
        state.isIndustryDataLoading = false;
        state.industriesCategoriesList = payload.items;
      })
      .addCase(getIndustryCategories.rejected, (state) => {
        state.isIndustryDataLoading = false;
      });
  },
});

export const { resetPagination } = caseStudiesSlice.actions;
export default caseStudiesSlice.reducer;
